import React, { useMemo, useState,useEffect } from "react";
import {
  Table,
  TableBody,
  TableHeader,
  TableHead,
  TableRow,
  TableCell,
  TableFooter,
} from "../ui/table";
import { Switch } from "../ui/switch";
import Card from "../ui/card-snippet";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { StatusTag, getStatusColor } from "../ui/statusTag";
import AdvancedTablePagination from "./components/advancedTablePagination";
import ActionButton from "./components/actionButton";
import Common from "../../helper/common";
import { SearchTypeEnum, PaymentStatusEnum } from "../../constants/appConstant";
import {
  ExportAsExcel,
  ExportAsPdf,
  ExportAsCsv,
  CopyToClipboard,
  CopyTextToClipboard,
  PrintDocument,
  ExcelToJsonConverter,
  FileUpload,
} from "react-export-table";

import { NiCopy, Csv, NiExcel, NiPdf, NiPrint } from "../../svg";

import  { useDebounce }   from "../../hooks/useDebounce"


const getStatusColorRow = (status) => {
  switch (status) {
    case PaymentStatusEnum.Paid:
      return "bg-[#d6ffe5]";
    case PaymentStatusEnum.Un_Paid:
      return "bg-[#ffe4e4]";
    case PaymentStatusEnum.Partial_Paid:
      return "bg-[#ffd5b8]";
    default:
      return "bg-[#fff]";
  }
};

const AdvanceTable = ({
  title,
  columns,
  getData,
  data,
  actionButtons = [],
  showPagination = false,
  addButton,
  showExportoptions = false,
  search = null,
  exportFileName,
}) => {
  const _columns = useMemo(() => columns.map((item) => item.title), [columns]);
  const _data = useMemo(
    () =>
      data?.data?.map((item) => columns.map((column) => item[column.key])) ||
      [],
    [data?.data, columns]
  );

  const [searchQuery, setSearchQuery] = useState("");

  
  const debouncedSearch = useDebounce(searchQuery, 500);
  
  useEffect(() => {
    if (search === SearchTypeEnum.Server_Search) {
      getData(1, debouncedSearch);
    }
  }, [debouncedSearch]);

  const filteredData = useMemo(() => {
    if (!searchQuery) return data?.data || [];
    return data?.data.filter((item) =>
      columns.some((column) =>
        item[column.key]
          ?.toString()
          ?.toLowerCase()
          ?.includes(searchQuery.toLowerCase())
      )
    );
  }, [searchQuery, data, columns]);

  // Calculate totals for columns with showTotal: true
  const columnTotals = useMemo(() => {
    const totals = {};
    const hasShowTotal = columns.some((column) => column.showTotal);

    if (!hasShowTotal || !filteredData.length) return totals;

    columns.forEach((column) => {
      if (column.showTotal && (column.type === "number" || !column.type)) {
        const sum = filteredData.reduce((acc, item) => {
          const value = parseFloat(item[column.key]) || 0;
          return acc + value;
        }, 0);
        totals[column.key] = sum;
      }
    });

    return totals;
  }, [columns, filteredData]);

  // Check if any column has showTotal enabled
  const hasAnyTotal = useMemo(
    () => columns.some((column) => column.showTotal),
    [columns]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    // if (search === SearchTypeEnum.Server_Search && getData) getData(1, value);
  };

  const ExportButton = ({ props, title }) => (
    <button {...props}>{title}</button>
  );

  const _renderExportOptions = () => {
    if (!showExportoptions) {
      return addButton ? addButton() : null;
    }

    return (
      <div className="w-full flex gap-0 items-center justify-center md:justify-end md:gap-0 ">
        <CopyToClipboard data={_data} headers={_columns}>
          {(props) => <ExportButton props={props} title={<NiCopy />} />}
        </CopyToClipboard>
        <ExportAsPdf
          data={_data}
          headers={_columns}
          fileName={exportFileName}
          headerStyles={{ fillColor: "red" }}
        >
          {(props) => <ExportButton props={props} title={<NiPdf />} />}
        </ExportAsPdf>

        <ExportAsExcel
          data={_data}
          headers={_columns}
          fileName={exportFileName}
        >
          {(props) => <ExportButton props={props} title={<NiExcel />} />}
        </ExportAsExcel>
        <div className="pr-0 md:pr-3">
          <PrintDocument
            data={_data}
            headers={_columns}
            className="pr-0 md:pr-3"
          >
            {(props) => <ExportButton props={props} title={<NiPrint />} />}
          </PrintDocument>
        </div>
        {addButton ? addButton() : null}
      </div>
    );
  };

  return (
    <div className="space-y-6 ">
      <Card
        title={title}
        addButton={_renderExportOptions}
        searchQuery={search !== null ? searchQuery : null}
        handleSearchChange={search !== null ? handleSearchChange : null}
      >
        <Table>
          <TableHeader>
            <TableRow className="bg-secondary">
              {columns.map((column) => (
                <TableHead
                  key={column.key}
                  className="font-semibold whitespace-nowrap "
                  style={column.style}
                >
                  {column.title}
                </TableHead>
              ))}

              {actionButtons.length ? (
                <TableHead className="text-right w-20 sticky right-0 bg-background drop-shadow-md ">
                  Action
                </TableHead>
              ) : null}
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredData.map((item, index) => (
              <TableRow key={index} className="hover:bg-muted">
                {columns.map((column, cellIndex) => {
                  switch (column.type) {
                    case "image":
                      return (
                        <TableCell
                          key={cellIndex}
                          className="font-medium text-card-foreground/80 whitespace-nowrap"
                        >
                          <div className="flex space-x-3 rtl:space-x-reverse items-center">
                            <a
                              href={Common.Utility.getImagePath(
                                item[column.key]
                              )}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="cursor-pointer hover:opacity-80 transition inline-block"
                            >
                              <Avatar className="rounded-lg">
                                <AvatarImage
                                  // src={
                                  //   Common.AppConstant.imgBaseurl +
                                  //   item[column.key]
                                  // }
                                  src={Common.Utility.getImagePath(
                                    item[column.key]
                                  )}
                                />
                                <AvatarFallback>AB</AvatarFallback>
                              </Avatar>
                            </a>
                            <span className="text-sm text-default-600">
                              {item.firstName}
                            </span>
                          </div>
                        </TableCell>
                      );
                    case "text":
                      return (
                        <TableCell
                          key={cellIndex}
                          className="whitespace-nowrap"
                          style={column.style}
                        >
                          {column.render
                            ? column.render(item)
                            : item[column.key]}
                        </TableCell>
                      );
                    case "toggle":
                      return (
                        <TableCell key={cellIndex}>
                          <Switch
                            checked={item[column.key]}
                            onCheckedChange={() => column.onChanged?.(item)}
                          />
                        </TableCell>
                      );
                    case "status":
                      return (
                        <TableCell key={cellIndex}>
                          <StatusTag
                            tagcolor={getStatusColor(item.status)}
                            text={item.status}
                          />
                        </TableCell>
                      );
                    default:
                      return (
                        <TableCell key={cellIndex} style={column.style}>
                          {item[column.key]}
                        </TableCell>
                      );
                  }
                })}
                {actionButtons.length > 0 && (
                  <TableCell
                    bgColor={getStatusColorRow(item.paymentStatusId)}
                    className="text-right sticky right-0 drop-shadow-md"
                  >
                    <div className="flex space-x-0 rtl:space-x-reverse gap-1">
                      {actionButtons
                        .filter((action) =>
                          action.visibleProp
                            ? item[action.visibleProp] === action.visibleValue
                            : true
                        )
                        .map((action) =>
                          action.type == "edit" ? (
                            <ActionButton
                              icon="heroicons:pencil"
                              onClick={action.onClick?.bind(this, item)}
                            />
                          ) : action.type == "custom" ? (
                            action.button(item)
                          ) : action.type == "customIcon" ? (
                            <ActionButton
                              icon={`heroicons:${action.icon}`}
                              onClick={action.onClick?.bind(this, item)}
                            />
                          ) : (
                            <ActionButton
                              icon={`heroicons:${
                                action.type == "delete" ? "trash" : "eye"
                              }`}
                              onClick={action.onClick?.bind(this, item)}
                            />
                          )
                        )}
                    </div>
                  </TableCell>
                )}
                {/* <TableCell className="text-right sticky right-0 bg-background drop-shadow-md">
                  <div className="flex space-x-3 rtl:space-x-reverse">
                    <ActionButton
                      icon="heroicons:pencil"
                      onClick={() => updateTrigger(item.id)}
                    />
                  </div>
                </TableCell> */}
              </TableRow>
            ))}
          </TableBody>

          {/* Table Footer with Totals */}
          {hasAnyTotal && filteredData.length > 0 && (
            <TableFooter>
              <TableRow className="bg-muted/50 font-semibold">
                {columns.map((column, index) => (
                  <TableCell
                    key={column.key}
                    className="font-semibold"
                    style={column.style}
                  >
                    {column.showTotal ? (
                      <span className="text-primary">
                        {index === 0 && "Total: "}
                        {columnTotals[column.key]?.toLocaleString() || "0"}
                      </span>
                    ) : index === 0 ? (
                      ""
                    ) : (
                      ""
                    )}
                  </TableCell>
                ))}

                {actionButtons.length > 0 && (
                  <TableCell className="text-right sticky right-0 bg-muted/50">
                    {/* Empty cell for action column */}
                  </TableCell>
                )}
              </TableRow>
            </TableFooter>
          )}
        </Table>
        {showPagination && data && (
          <AdvancedTablePagination
            currentPage={data.pageNumber}
            onPageChanged={getData}
            totalPages={data.totalPages}
            displayCount={data.totalDisplayed / data.pageNumber}
            // displayCount={data.pageSize}
            clubcustomers
          />
        )}
      </Card>
    </div>
  );
};
export default AdvanceTable;
